import React, { useEffect, useState } from "react";
import { adminApi, SOURCE_META, STATUS_META, srcLabel } from "./api";
import {
  X, Loader2, Link2, Send, Trash2, Mail, Tag as TagIcon, Plus, Eye, EyeOff, Clock,
} from "lucide-react";

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-[14px] font-medium text-slate-800 break-words">{value || "—"}</p>
  </div>
);

const SD = ({ label, value }) => value ? (
  <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-[13px] text-slate-500">{label}</span>
    <span className="text-[13px] font-semibold text-slate-800 text-right break-words">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
  </div>
) : null;

export default function LeadDetail({ leadId, statuses, onClose, onChanged, onShare }) {
  const [d, setD] = useState(null);
  const [showScores, setShowScores] = useState(false);
  const [note, setNote] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.lead(leadId).then(setD);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  if (!d) return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-violet-500" /></div>
    </div>
  );

  const lead = d.lead;
  const sd = lead.source_data || {};
  const meta = SOURCE_META[lead.source] || {};
  const isDiscover = lead.source === "discover_assessment";

  const refresh = async () => { await load(); onChanged && onChanged(); };

  const changeStatus = async (s) => { setBusy(true); await adminApi.setStatus(leadId, s); await refresh(); setBusy(false); };
  const addNote = async () => { if (!note.trim()) return; setBusy(true); await adminApi.addNote(leadId, note.trim()); setNote(""); await refresh(); setBusy(false); };
  const addTag = async () => { if (!tag.trim()) return; setBusy(true); await adminApi.addTag(leadId, tag.trim()); setTag(""); await refresh(); setBusy(false); };
  const rmTag = async (t) => { await adminApi.removeTag(leadId, t); await refresh(); };
  const assign = async (v) => { await adminApi.assign(leadId, v || null); await refresh(); };
  const resend = async () => { setBusy(true); try { await adminApi.resend(leadId); alert("Report re-sent to parent."); } catch (e) { alert("Could not resend."); } setBusy(false); };
  const del = async () => { if (!window.confirm("Move this lead to the Deleted bin?")) return; await adminApi.del([leadId]); onChanged && onChanged(); onClose(); };

  // Discover source-specific rendering
  const discoverBlock = () => {
    const bands = sd.domain_bands || {};
    const scores = sd.domain_scores || {};
    const core = sd.core_domains || [];
    const codes = Object.keys(bands).length ? Object.keys(bands) : Object.keys(scores);
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[13px] font-bold text-slate-700">Learning strengths (band words)</p>
          <button onClick={() => setShowScores((v) => !v)} data-testid="toggle-scores"
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-violet-600">
            {showScores ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {showScores ? "Hide scores" : "Show scores (internal)"}
          </button>
        </div>
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          {codes.map((c) => (
            <div key={c} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-0 text-[13px]">
              <span className="font-semibold text-slate-700">{c} {core.includes(c) && <span className="ml-1 rounded bg-violet-100 text-violet-700 px-1.5 text-[10px] font-bold">CORE</span>}</span>
              <span className="flex items-center gap-3">
                <span className="text-slate-600">{bands[c] || "—"}</span>
                {showScores && <span className="font-mono text-[12px] text-slate-400" data-testid={`score-${c}`}>{scores[c] ?? "—"}</span>}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[13px]">
          <SD label="Child" value={sd.child_name} />
          <SD label="Grade" value={sd.grade} />
          <SD label="Duration (s)" value={sd.duration_seconds} />
          {sd.report_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${sd.report_url}`} target="_blank" rel="noreferrer" className="text-[12px] text-violet-600 font-semibold col-span-2">View parent report data →</a>}
        </div>
      </div>
    );
  };

  const genericBlock = () => (
    <div className="rounded-xl border border-slate-200 p-3">
      {Object.entries(sd).map(([k, v]) => <SD key={k} label={k.replace(/_/g, " ")} value={v} />)}
      {Object.keys(sd).length === 0 && <p className="text-[13px] text-slate-400">No extra fields.</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-[12px] font-bold ${meta.chip || "bg-slate-200 text-slate-700"}`}>{srcLabel(lead.source)}</span>
            <span className={`rounded-full px-2.5 py-1 text-[12px] font-bold ${STATUS_META[lead.status] || "bg-slate-100 text-slate-600"}`}>{lead.status}</span>
          </div>
          <button onClick={onClose} data-testid="detail-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">{lead.name || "—"}</h2>
            <p className="text-[12px] text-slate-400">{new Date(lead.created_at).toLocaleString()}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={lead.email} />
            <Field label="Phone" value={lead.phone} />
            <Field label="City" value={lead.city} />
            <Field label="Source page" value={lead.source_page} />
          </div>

          {/* Actions row */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => onShare([leadId])} data-testid="detail-share" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-3 py-1.5 text-[13px] font-semibold hover:bg-slate-700"><Send className="h-3.5 w-3.5" /> Share</button>
            {isDiscover && <button onClick={resend} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><Mail className="h-3.5 w-3.5" /> Resend email</button>}
            <button onClick={del} data-testid="detail-delete" className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 text-rose-600 px-3 py-1.5 text-[13px] font-semibold hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </div>

          {/* Status + assign */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Status</p>
              <select value={lead.status} onChange={(e) => changeStatus(e.target.value)} data-testid="detail-status-select"
                className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-[13px] font-medium">
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Assigned to</p>
              <input defaultValue={lead.assigned_to || ""} onBlur={(e) => assign(e.target.value)} placeholder="unassigned"
                className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-[13px]" />
            </div>
          </div>

          {/* Source-specific */}
          <div>
            <p className="text-[13px] font-bold text-slate-700 mb-2">Details</p>
            {isDiscover ? discoverBlock() : genericBlock()}
          </div>

          {/* Tags */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Tags</p>
            <div className="flex flex-wrap gap-1.5 items-center">
              {(lead.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[12px] font-semibold text-slate-700">
                  <TagIcon className="h-3 w-3" /> {t}
                  <button onClick={() => rmTag(t)} className="ml-0.5 text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button>
                </span>
              ))}
              <div className="inline-flex items-center gap-1">
                <input value={tag} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="add tag"
                  className="w-24 rounded-full border border-slate-200 px-2.5 py-1 text-[12px]" data-testid="detail-tag-input" />
                <button onClick={addTag} className="p-1 rounded-full bg-slate-100 hover:bg-slate-200"><Plus className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </div>

          {/* Linked */}
          {d.linked.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <p className="text-[13px] font-bold text-amber-800 flex items-center gap-1.5 mb-2"><Link2 className="h-4 w-4" /> This contact has other activity</p>
              <div className="space-y-1">
                {d.linked.map((l) => (
                  <div key={l.lead_id} className="flex items-center justify-between text-[12px]">
                    <span className="font-semibold text-slate-700">{srcLabel(l.source)}</span>
                    <span className="text-slate-500">{(l.created_at || "").slice(0, 10)} · {l.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <p className="text-[13px] font-bold text-slate-700 mb-2">Notes</p>
            <div className="space-y-2 mb-2">
              {(lead.notes || []).map((n, i) => (
                <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
                  <p className="text-[13px] text-slate-700">{n.text}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{n.by} · {new Date(n.at).toLocaleString()}</p>
                </div>
              ))}
              {(lead.notes || []).length === 0 && <p className="text-[13px] text-slate-400">No notes yet.</p>}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add an internal note…"
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px]" data-testid="detail-note-input" />
              <button onClick={addNote} disabled={busy} className="rounded-lg bg-violet-600 text-white px-3 text-[13px] font-semibold hover:bg-violet-700">Add</button>
            </div>
          </div>

          {/* Activity */}
          <div>
            <p className="text-[13px] font-bold text-slate-700 mb-2 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Activity</p>
            <div className="space-y-1.5">
              {d.activity.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-[12px] text-slate-500">
                  <span className="font-medium text-slate-600">{a.action.replace(/_/g, " ")}</span>
                  <span>{a.actor} · {new Date(a.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
